import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BusinessPlanSection, SectionId } from '../../types/businessPlan';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Heading from '@tiptap/extension-heading';

interface BusinessPlanSectionEditorProps {
  section: BusinessPlanSection;
  onSave: (sectionId: SectionId, content: string) => Promise<boolean>;
  onCancel: () => void;
  isLoading?: boolean;
  onChange?: (html: string) => void;
}

const BusinessPlanSectionEditor: React.FC<BusinessPlanSectionEditorProps> = ({
  section,
  onSave,
  onCancel,
  isLoading = false,
  onChange
}) => {
  const initialContent = useMemo(() => section.content || '', [section.content]);
  const [content, setContent] = useState<string>(section.content || '');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const lastSavedHtmlRef = useRef<string>(section.content || '');
  const hasInitializedRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Heading.configure({ levels: [1, 2, 3] }),
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setContent(html);
      if (onChange) onChange(html);
      if (error) setError(null);
    },
  });

  useEffect(() => {
    // Update content if section changes
    const next = section.content || '';
    setContent(next);
    if (onChange) onChange(next);
    lastSavedHtmlRef.current = next;
    if (editor && editor.getHTML() !== next) {
      editor.commands.setContent(next || '<p></p>');
    }
  }, [section]);

  useEffect(() => {
    if (!editor) return;
    if (isLoading || isSaving) return;

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      return;
    }

    const current = editor.getHTML();
    if (current === lastSavedHtmlRef.current) return;

    const t = window.setTimeout(async () => {
      try {
        setIsAutoSaving(true);
        const success = await onSave(section.id, current);
        if (success) {
          lastSavedHtmlRef.current = current;
          setLastSavedAt(new Date().toLocaleTimeString());
          if (error) setError(null);
        }
      } catch (e) {
      } finally {
        setIsAutoSaving(false);
      }
    }, 1500);

    return () => window.clearTimeout(t);
  }, [content, editor, error, isLoading, isSaving, onSave, section.id]);

  const handleSave = async () => {
    const html = editor ? editor.getHTML() : content;
    if (!html.trim() || html.trim() === '<p></p>') {
      setError('Content cannot be empty');
      return;
    }

    try {
      setIsSaving(true);
      const success = await onSave(section.id, html);
      if (!success) {
        setError('Failed to save changes. Please try again.');
      } else {
        lastSavedHtmlRef.current = html;
        setLastSavedAt(new Date().toLocaleTimeString());
        onCancel();
      }
    } catch (err) {
      console.error('Error saving section:', err);
      setError('An unexpected error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-4">Edit {section.title}</h2>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      <div className="mb-4">
        <label htmlFor="section-content" className="block text-sm font-medium text-gray-700 mb-2">
          Content
        </label>
        <div className="mb-2 text-xs text-gray-500">
          {isAutoSaving ? 'Auto-saving…' : lastSavedAt ? `Saved at ${lastSavedAt}` : 'Auto-save is on'}
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className="px-2 py-1 rounded border border-gray-200 text-sm"
            disabled={!editor || isSaving || isLoading}
          >
            Bold
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className="px-2 py-1 rounded border border-gray-200 text-sm"
            disabled={!editor || isSaving || isLoading}
          >
            Italic
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className="px-2 py-1 rounded border border-gray-200 text-sm"
            disabled={!editor || isSaving || isLoading}
          >
            Bullets
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            className="px-2 py-1 rounded border border-gray-200 text-sm"
            disabled={!editor || isSaving || isLoading}
          >
            Numbered
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            className="px-2 py-1 rounded border border-gray-200 text-sm"
            disabled={!editor || isSaving || isLoading}
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            className="px-2 py-1 rounded border border-gray-200 text-sm"
            disabled={!editor || isSaving || isLoading}
          >
            H3
          </button>
        </div>
        <div
          id="section-content"
          className="w-full min-h-[420px] p-3 border border-gray-300 rounded-md focus-within:ring-indigo-500 focus-within:border-indigo-500 prose max-w-none"
        >
          <EditorContent editor={editor} />
        </div>
      </div>
      
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isSaving || isLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isSaving || isLoading}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default BusinessPlanSectionEditor;
